import React, { useState, useRef, useEffect } from 'react';
import { FileItem } from '../../types/files';
import { useTranslation } from 'react-i18next';
import { readFileTree } from '../../utils/fileTree';
import { FileNode } from './FileNode';
import { FaRegFile, FaRegFolder } from 'react-icons/fa';

interface FolderNodeProps {
  item: FileItem;
  level: number;
  isSelected: boolean;
  onFileClick: (file: FileItem, options?: { preview?: boolean }) => void;
  onContextMenu: (e: React.MouseEvent, item: FileItem) => void;
  onDragStart: (path: string) => void;
  onDragOver: (e: React.DragEvent, path: string) => void;
  onDragLeave: (e: React.DragEvent, path: string) => void;
  onDrop: (e: React.DragEvent, item: FileItem) => void;
  onSelect: (path: string) => void;
  renameItem: FileItem | null;
  renameValue: string;
  onRenameChange: (value: string) => void;
  onRenameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRenameBlur: () => void;
  dropTargetPath: string | null;
  localActiveFile: FileItem | null;
  previewTab: FileItem | null;
  newItemType: 'file' | 'folder' | null;
  newItemParentPath: string | null;
  newItemName: string;
  onNewItemNameChange: (value: string) => void;
  onNewItemKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onNewItemBlur: () => void;
  newItemInputRef: React.RefObject<HTMLInputElement>;
  searchQuery: string;
  shouldShowItem: (item: FileItem) => boolean;
  sortFileTree: (items: FileItem[]) => FileItem[];
  selectedItemPath: string | null; // Bu prop'u ekledik
  onCollapseAll?: () => void;
}

export const FolderNode = React.memo<FolderNodeProps>(({
  item,
  level,
  isSelected,
  onFileClick,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onSelect,
  renameItem,
  renameValue,
  onRenameChange,
  onRenameKeyDown,
  onRenameBlur,
  dropTargetPath,
  localActiveFile,
  previewTab,
  newItemType,
  newItemParentPath,
  newItemName,
  onNewItemNameChange,
  onNewItemKeyDown,
  onNewItemBlur,
  newItemInputRef,
  searchQuery,
  shouldShowItem,
  sortFileTree,
  selectedItemPath, // Bu prop'u aldık
  onCollapseAll
}) => {
  const { t } = useTranslation('FileExplorer');
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Her folder kendi state'ini yönetir
  const [isExpanded, setIsExpanded] = useState(false);
  const [children, setChildren] = useState<FileItem[] | null>((item as any).children || null);
  const [isLoading, setIsLoading] = useState(false);

  const isDropTarget = dropTargetPath === item.path;

  useEffect(() => {
    if (renameItem && renameItem.path === item.path && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renameItem, item.path]);

  // Collapse all effect
  useEffect(() => {
    if (onCollapseAll) {
      const handleCollapseAll = () => {
        setIsExpanded(false);
      };
      // Bu effect sadece collapse all durumunda çalışır
    }
  }, [onCollapseAll]);

  const handleToggle = async () => {
    // Eğer açıksa kapat
    if (isExpanded) {
      setIsExpanded(false);
      onSelect(item.path);
      return;
    }

    // Kapalıysa aç
    setIsExpanded(true);
    onSelect(item.path);

    // Eğer children yoksa API'dan yükle
    if (!children) {
      setIsLoading(true);
      try {
        const loadedChildren = await readFileTree(item.path);
        const sortedChildren = sortFileTree(loadedChildren);
        setChildren(sortedChildren);
      } catch (e) {
        console.error('Error loading folder children:', e);
        setChildren([]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Collapse all fonksiyonu
  const collapseAll = () => {
    setIsExpanded(false);
  };

  // Search filtreleme
  const filterFiles = (items: FileItem[]): FileItem[] => {
    if (!searchQuery) return items;
    
    return items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (item.isDirectory && children) {
        const filteredChildren = filterFiles(children);
        return matchesSearch || filteredChildren.length > 0;
      }
      return matchesSearch;
    });
  };

  const getNodes = (): React.ReactNode[] => {
    if (!children || !isExpanded) return [];

    const filteredItems = filterFiles(children.filter(shouldShowItem));
    const nodes: React.ReactNode[] = [];

    for (const child of filteredItems) {
      if (child.isDirectory) {
        nodes.push(
          <FolderNode
            key={child.path}
            item={child}
            level={level + 1}
            isSelected={selectedItemPath === child.path} // DÜZELTME: Her child kendi seçili durumunu kontrol ediyor
            onFileClick={onFileClick}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onSelect={onSelect}
            renameItem={renameItem}
            renameValue={renameValue}
            onRenameChange={onRenameChange}
            onRenameKeyDown={onRenameKeyDown}
            onRenameBlur={onRenameBlur}
            dropTargetPath={dropTargetPath}
            localActiveFile={localActiveFile}
            previewTab={previewTab}
            newItemType={newItemType}
            newItemParentPath={newItemParentPath}
            newItemName={newItemName}
            onNewItemNameChange={onNewItemNameChange}
            onNewItemKeyDown={onNewItemKeyDown}
            onNewItemBlur={onNewItemBlur}
            newItemInputRef={newItemInputRef}
            searchQuery={searchQuery}
            shouldShowItem={shouldShowItem}
            sortFileTree={sortFileTree}
            selectedItemPath={selectedItemPath} // DÜZELTME: selectedItemPath prop'unu geçiyoruz
          />
        );
      } else {
        nodes.push(
          <FileNode
            key={child.path}
            item={child}
            level={level + 1}
            isActive={localActiveFile?.path === child.path}
            isSelected={selectedItemPath === child.path} // DÜZELTME: Her child kendi seçili durumunu kontrol ediyor
            isPreview={previewTab?.path === child.path}
            onFileClick={onFileClick}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onSelect={onSelect}
            renameItem={renameItem}
            renameValue={renameValue}
            onRenameChange={onRenameChange}
            onRenameKeyDown={onRenameKeyDown}
            onRenameBlur={onRenameBlur}
            dropTargetPath={dropTargetPath}
          />
        );
      }
    }

    // New item input'u folder içinde göster
    if (newItemType && newItemParentPath === item.path) {
      nodes.push(
        <div className="file-tree-item new-item" key={`new-item-${item.path}`} style={{ paddingLeft: `${(level + 1) * 15}px` }}>
          <span className="file-icon">
            {newItemType === 'file' ? 
              <FaRegFile className="icon" /> : 
              <FaRegFolder className="icon" />
            }
          </span>
          <input
            ref={newItemInputRef}
            value={newItemName}
            onChange={(e) => onNewItemNameChange(e.target.value)}
            onKeyDown={onNewItemKeyDown}
            placeholder={t(`placeholders.${newItemType === 'file' ? 'newFile' : 'newFolder'}`)}
            className="new-item-input"
            autoFocus
            onBlur={onNewItemBlur}
            style={{ width: 140 }}
          />
        </div>
      );
    }

    return nodes;
  };

  return (
    <div key={item.path}>
      <div
        className={`file-tree-item folder${isExpanded ? ' expanded' : ''}${isDropTarget ? ' drop-target' : ''}${isSelected ? ' selected' : ''}`}
        style={{ paddingLeft: `${level * 15}px` }}
        onClick={handleToggle}
        onContextMenu={(e) => onContextMenu(e, item)}
        draggable
        onDragStart={() => onDragStart(item.path)}
        onDragOver={(e) => onDragOver(e, item.path)}
        onDragLeave={(e) => onDragLeave(e, item.path)}
        onDrop={(e) => onDrop(e, item)}
      >
        <span className="file-icon">
          {isLoading ? (
            <span className="folder-icon loading">⏳</span>
          ) : (
            <span className="folder-icon">{isExpanded ? '📂' : '📁'}</span>
          )}
        </span>
        {renameItem && renameItem.path === item.path ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={onRenameKeyDown}
            className="new-item-input rename-input"
            autoFocus
            onBlur={onRenameBlur}
            style={{ width: 140 }}
          />
        ) : (
          <span>{item.name}</span>
        )}
      </div>
      
      {/* Children'ı render et */}
      {getNodes()}
    </div>
  );
});

// Collapse all için ref expose et
export interface FolderNodeRef {
  collapseAll: () => void;
}